import { renderTemplateContent } from "../contentRenderer";
import { mergeTemplateDesign, templateDesignToCssVars } from "../design";
import { DefaultTemplateLink } from "../templateLink";
import { WebBuilderPageRegion } from "../WebBuilderPageRegion";
import { WebGlobalBuilderRegion } from "../WebGlobalBuilderRegion";
import { RestaurantMobileMenu } from "./RestaurantMobileMenu";
import { RestaurantSteakhouseStyles } from "./RestaurantSteakhouseStyles";
import type { ReactNode } from "react";
import type {
    ResolvedTemplateDesign,
    SiteBuilderContentJson,
    SiteTemplateDefinition,
    TemplateLinkComponent,
    TemplateRenderProps,
} from "../types";

function RestaurantStyles() {
    return (
        <style>{`
            [data-web-template-family="restaurant"] {
                container-name: restaurant-template;
                container-type: inline-size;
            }

            [data-web-template-family="restaurant"] .site-builder-section {
                position: relative;
                padding: clamp(4rem, 8vw, 7rem) 1.25rem;
            }

            [data-web-template-family="restaurant"] .site-builder-grid {
                width: min(100%, var(--site-container));
                margin-inline: auto;
                display: grid;
                gap: clamp(1rem, 2.4vw, 2rem);
            }

            [data-web-template-family="restaurant"] .site-builder-grid:not(.site-builder-grid-placed) {
                grid-template-columns: repeat(12, minmax(0, 1fr));
            }

            [data-web-template-family="restaurant"] .site-builder-grid:not(.site-builder-grid-placed) > .site-builder-grid-item {
                grid-column: 1 / -1;
            }

            [data-web-template-family="restaurant"] .site-builder-column {
                display: grid;
                align-content: start;
                gap: 1rem;
                min-width: 0;
            }

            [data-web-template-family="restaurant"] .site-builder-column-inline {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
            }

            [data-web-template-family="restaurant"] [data-document-role="openingHoursTable"] > .site-builder-column-inline {
                align-items: center;
                flex-direction: row;
                flex-wrap: nowrap;
                justify-content: space-between;
                width: 100%;
            }

            [data-web-template-family="restaurant"] [data-document-role="openingHoursTable"] > .site-builder-column-inline > [data-site-builder-block="text"] {
                flex: 1 1 0;
                min-width: 0;
            }

            [data-web-template-family="restaurant"] [data-document-role="openingHoursTable"] > .site-builder-column-inline .site-builder-text {
                width: 100% !important;
            }

            [data-web-template-family="restaurant"] [data-document-role="heroCopy"]:has(> [data-document-role="heroHeading"]) {
                grid-template-columns: repeat(12, minmax(0, 1fr));
                column-gap: 12px;
                row-gap: 24px;
                width: 100%;
            }

            [data-web-template-family="restaurant"] [data-document-role="heroHeading"],
            [data-web-template-family="restaurant"] [data-document-role="heroDescription"] {
                grid-column: 1 / -1;
            }

            [data-web-template-family="restaurant"] [data-document-role="heroPrimaryAction"] {
                grid-column: 1 / span 6;
            }

            [data-web-template-family="restaurant"] [data-document-role="heroSecondaryAction"] {
                grid-column: 7 / span 6;
            }

            [data-web-template-family="restaurant"] [data-document-role="heroHighlightOne"] {
                grid-column: 1 / span 4;
            }

            [data-web-template-family="restaurant"] [data-document-role="heroHighlightTwo"] {
                grid-column: 5 / span 4;
            }

            [data-web-template-family="restaurant"] [data-document-role="heroHighlightThree"] {
                grid-column: 9 / span 4;
            }

            [data-web-template-family="restaurant"] [data-document-role="heroPrimaryAction"],
            [data-web-template-family="restaurant"] [data-document-role="heroSecondaryAction"],
            [data-web-template-family="restaurant"] [data-document-role="heroHighlightOne"],
            [data-web-template-family="restaurant"] [data-document-role="heroHighlightTwo"],
            [data-web-template-family="restaurant"] [data-document-role="heroHighlightThree"] {
                justify-self: start;
            }

            [data-web-template-family="restaurant"] .site-builder-card {
                position: relative;
                display: grid;
                min-width: 0;
                align-content: start;
            }

            [data-web-template-family="restaurant"] .site-builder-card-content-inline > .site-builder-column {
                flex: 1 1 auto;
            }

            [data-web-template-family="restaurant"] .site-builder-card-content-inline > .site-builder-badge {
                flex: 0 0 auto;
                min-width: max-content;
                padding: 0.7rem 1.15rem !important;
                font-size: 0.75rem !important;
                line-height: 1.1 !important;
                white-space: nowrap;
                box-shadow: 0 8px 22px rgb(0 0 0 / 0.18);
            }

            [data-web-template-family="restaurant"] .site-builder-card-content > .site-builder-column-inline {
                justify-content: space-between;
            }

            [data-web-template-family="restaurant"] .site-builder-navigation {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 1.5rem;
            }

            [data-web-template-family="restaurant"] .site-builder-navigation-item {
                display: inline-flex;
            }

            [data-web-template-family="restaurant"] .site-builder-navigation-link {
                color: color-mix(in srgb, var(--site-text) 72%, transparent);
                font-size: .875rem;
                font-weight: 700;
                transition: color 180ms ease;
            }

            [data-web-template-family="restaurant"] .site-builder-navigation-link:hover {
                color: var(--site-text);
            }

            @container restaurant-template (max-width: 640px) {
                [data-web-template-family="restaurant"] .site-builder-card-content-inline {
                    align-items: flex-start;
                    flex-direction: column;
                }

                [data-web-template-family="restaurant"] [data-document-role="heroCopy"]:has(> [data-document-role="heroHeading"]) {
                    grid-template-columns: minmax(0, 1fr);
                }

                [data-web-template-family="restaurant"] [data-document-role="heroHeading"],
                [data-web-template-family="restaurant"] [data-document-role="heroDescription"],
                [data-web-template-family="restaurant"] [data-document-role="heroPrimaryAction"],
                [data-web-template-family="restaurant"] [data-document-role="heroSecondaryAction"],
                [data-web-template-family="restaurant"] [data-document-role="heroHighlightOne"],
                [data-web-template-family="restaurant"] [data-document-role="heroHighlightTwo"],
                [data-web-template-family="restaurant"] [data-document-role="heroHighlightThree"] {
                    grid-column: 1;
                }
            }

            [data-web-template-family="restaurant"] [data-document-role="brand"],
            [data-web-template-family="restaurant"] [data-document-role="brandCopy"] {
                min-width: 0;
            }

            [data-web-template-family="restaurant"] [data-web-region="header"] .site-builder-navigation {
                gap: .75rem;
                justify-content: center;
            }

            [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerNavigation"] {
                align-items: center;
                flex-wrap: nowrap;
                justify-content: center;
                justify-self: center;
                max-width: 100%;
                width: max-content;
            }

            [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerNavigation"] > [data-site-builder-block="text"] {
                flex: 0 0 auto;
            }

            [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text {
                display: inline-flex;
                padding: .15rem 0;
                position: relative;
                text-decoration: none;
            }

            [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text::after {
                background: var(--site-accent);
                border-radius: 999px;
                bottom: -.25rem;
                content: "";
                height: 2px;
                left: 50%;
                position: absolute;
                transform: translateX(-50%) scaleX(0);
                transform-origin: center;
                transition: transform 180ms ease;
                width: 100%;
            }

            [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text:hover::after {
                transform: translateX(-50%) scaleX(1);
            }

            [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text > span {
                transition: color 180ms ease;
            }

            [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text:hover > span {
                color: var(--site-text) !important;
            }

            [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="sectionNavigation"] {
                justify-content: flex-end;
            }

            [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="pageNavigation"] {
                justify-content: flex-start;
            }

            [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerActions"] {
                flex-wrap: nowrap;
                justify-content: flex-end;
            }

            [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerCta"] {
                flex: 0 0 auto;
                width: auto !important;
            }

            [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="localeActions"] {
                flex: 0 0 auto;
            }

            [data-web-template-family="restaurant"] .restaurant-mobile-menu-toggle {
                display: none;
            }

            @container restaurant-template (min-width: 901px) {
                [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerNavigation"] {
                    background: transparent !important;
                    border: 0 !important;
                    border-radius: 0 !important;
                    gap: clamp(1.4rem, 2.4vw, 2.5rem) !important;
                    padding: .5rem 0 !important;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text {
                    letter-spacing: .015em;
                    padding: .35rem 0;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text::after {
                    bottom: -.15rem;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerActions"],
                [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerCta"] {
                    justify-content: flex-end !important;
                }
            }

            [data-web-template-family="restaurant"] [data-web-region="hero"] {
                display: grid;
                align-items: center;
            }

            [data-web-template-family="restaurant"] [data-web-region="footer"] .site-builder-navigation {
                flex-direction: column;
                align-items: flex-start;
                gap: .65rem;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="footer"] :where(.site-builder-text, .site-builder-text > span, .site-builder-icon) {
                color: #fffaf0 !important;
            }

            [data-web-template="restaurant-brunch"] [data-web-region="footer"] a:hover :where(.site-builder-text, .site-builder-text > span) {
                color: #f2c675 !important;
            }

            @container restaurant-template (max-width: 900px) {
                [data-web-template-family="restaurant"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="sectionNavigation"]),
                [data-web-template-family="restaurant"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="pageNavigation"]) {
                    display: none;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                    grid-template-rows: auto !important;
                    align-items: center;
                    row-gap: 0;
                    padding-right: 3.75rem;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: none;
                    grid-column: 1 !important;
                    grid-row: 3 !important;
                    min-width: 0;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                    display: none;
                    grid-column: 1 !important;
                    grid-row: 2 !important;
                    min-width: 0;
                    width: 100%;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: block;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-mobile-menu-open] {
                    animation: restaurant-mobile-menu-reveal 220ms ease-out;
                    background:
                        radial-gradient(circle at 90% 8%, color-mix(in srgb, var(--site-accent) 13%, transparent), transparent 30rem),
                        linear-gradient(145deg, var(--site-surface), var(--site-background) 72%);
                    bottom: 0;
                    box-sizing: border-box;
                    height: 100dvh;
                    inset-inline: 0;
                    overflow-y: auto;
                    padding: 14px 1.25rem 24px !important;
                    position: fixed !important;
                    top: 0 !important;
                    width: 100%;
                    z-index: 100;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-mobile-menu-open] > .site-builder-grid {
                    align-content: stretch;
                    grid-template-rows: auto minmax(0, 1fr) auto !important;
                    min-height: calc(100dvh - 38px);
                    padding-right: 0;
                    width: min(100%, 42rem);
                }

                [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    padding-right: 3.75rem;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                    align-self: center;
                    animation: restaurant-mobile-menu-content 280ms 40ms ease-out both;
                    padding-block: clamp(2rem, 8dvh, 5rem);
                }

                [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    animation: restaurant-mobile-menu-content 280ms 90ms ease-out both;
                    align-self: end;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerNavigation"] {
                    box-sizing: border-box;
                    align-items: stretch;
                    background: transparent !important;
                    border: 0 !important;
                    border-radius: 0 !important;
                    flex-direction: column;
                    gap: 0 !important;
                    justify-content: flex-start;
                    justify-self: stretch;
                    max-width: 100%;
                    padding: 0 !important;
                    width: 100%;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerNavigation"] > [data-site-builder-block="text"] {
                    border-bottom: 1px solid color-mix(in srgb, var(--site-text) 13%, transparent);
                    padding: clamp(.85rem, 2.4dvh, 1.25rem) .1rem;
                    width: 100%;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerNavigation"] > [data-site-builder-block="text"]:last-child {
                    border-bottom: 0;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text {
                    align-items: center;
                    display: flex;
                    justify-content: space-between;
                    padding: 0;
                    width: 100% !important;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text > span {
                    color: var(--site-text) !important;
                    font-family: var(--site-heading-font) !important;
                    font-size: clamp(1.75rem, 7vw, 2.65rem) !important;
                    font-weight: 600 !important;
                    letter-spacing: -.025em;
                    line-height: 1.08 !important;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerActions"] {
                    align-items: center;
                    background: transparent;
                    border: 0;
                    border-radius: 0;
                    border-top: 1px solid color-mix(in srgb, var(--site-text) 13%, transparent);
                    flex-wrap: wrap;
                    gap: .75rem;
                    justify-content: space-between;
                    max-width: 100%;
                    padding: 1rem 0 0 !important;
                    width: 100%;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerCta"] {
                    min-height: 46px;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="localeActions"] {
                    min-width: 0;
                }

                [data-web-template-family="restaurant"] [data-document-role="brandCopy"] .site-builder-text {
                    max-width: min(64vw, 20rem);
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] .restaurant-mobile-menu-toggle {
                    align-items: center;
                    backdrop-filter: blur(14px);
                    background: color-mix(in srgb, var(--site-surface) 78%, transparent);
                    border: 1px solid color-mix(in srgb, var(--site-text) 16%, transparent);
                    border-radius: 999px;
                    cursor: pointer;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    height: 44px;
                    justify-content: center;
                    padding: 0;
                    position: absolute;
                    right: 1.25rem;
                    top: 16px;
                    transition: background 180ms ease, border-color 180ms ease, transform 180ms ease;
                    width: 44px;
                    z-index: 2;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] .restaurant-mobile-menu-toggle:hover {
                    background: color-mix(in srgb, var(--site-text) 11%, var(--site-surface));
                    border-color: color-mix(in srgb, var(--site-text) 28%, transparent);
                    transform: scale(1.03);
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] .restaurant-mobile-menu-toggle > span {
                    background: var(--site-text);
                    border-radius: 999px;
                    display: block;
                    height: 2px;
                    transition: transform 180ms ease, opacity 180ms ease;
                    width: 19px;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-mobile-menu-open] .restaurant-mobile-menu-toggle > span:first-child {
                    transform: translateY(6px) rotate(45deg);
                }

                [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-mobile-menu-open] .restaurant-mobile-menu-toggle > span:nth-child(2) {
                    opacity: 0;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-mobile-menu-open] .restaurant-mobile-menu-toggle > span:last-child {
                    transform: translateY(-6px) rotate(-45deg);
                }

                @keyframes restaurant-mobile-menu-reveal {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes restaurant-mobile-menu-content {
                    from {
                        opacity: 0;
                        transform: translateY(12px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-mobile-menu-open],
                    [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item {
                        animation: none !important;
                    }
                }
            }

            @container restaurant-template (max-width: 767px) {
                [data-web-template-family="restaurant"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] {
                    display: grid;
                    grid-template-columns: minmax(0, 1fr) !important;
                    grid-template-rows: none !important;
                    grid-auto-rows: auto !important;
                }

                [data-web-template-family="restaurant"] .site-builder-column-group {
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template-family="restaurant"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] > .site-builder-grid-item {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template-family="restaurant"] .site-builder-footer-empty-extended-slot {
                    inset: auto !important;
                    height: auto !important;
                    min-height: 44px !important;
                    position: relative !important;
                    width: 100% !important;
                }

                [data-web-template-family="restaurant"] .site-builder-column-inline[data-site-builder-stack-on-mobile="true"] {
                    align-items: stretch;
                    flex-direction: column;
                }

                [data-web-template-family="restaurant"] .site-builder-card-layout-left,
                [data-web-template-family="restaurant"] .site-builder-card-layout-right {
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template-family="restaurant"] .site-builder-navigation {
                    gap: .8rem;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                    grid-template-rows: auto !important;
                    align-items: center;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    grid-column: 1 !important;
                    grid-row: 3 !important;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                    grid-column: 1 / -1 !important;
                    grid-row: 2 !important;
                }

                [data-web-template-family="restaurant"] [data-document-role="brandCopy"] .site-builder-text {
                    max-width: min(52vw, 14rem);
                }
            }

            @container restaurant-template (max-width: 480px) {
                [data-web-template-family="restaurant"] [data-web-region="header"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                    grid-template-rows: auto !important;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    grid-column: 1 !important;
                    grid-row: 3 !important;
                    width: 100%;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                    grid-column: 1 !important;
                    grid-row: 2 !important;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="headerActions"] {
                    justify-content: space-between;
                    max-width: 100%;
                    width: 100%;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] [data-document-role="localeActions"] {
                    justify-content: flex-end;
                }

                [data-web-template-family="restaurant"] [data-document-role="brandCopy"] > [data-site-builder-block="text"]:last-child {
                    display: none;
                }

                [data-web-template-family="restaurant"] [data-document-role="brandCopy"] .site-builder-text {
                    max-width: min(68vw, 13rem);
                }
            }

            /* Alcatraz-inspired image-first opening and early compact navigation swap. */
            [data-web-template-family="restaurant"] [data-web-region="header"] {
                backdrop-filter: none !important;
                background: linear-gradient(180deg, rgba(3, 4, 6, .88), rgba(3, 4, 6, .34) 64%, transparent) !important;
                border-color: transparent !important;
                left: 0;
                min-height: 104px !important;
                position: fixed !important;
                right: 0;
                top: 0 !important;
                transition: background 420ms ease, backdrop-filter 420ms ease, min-height 420ms ease, box-shadow 420ms ease;
                width: 100%;
                z-index: 70 !important;
            }

            [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-header-scrolled] {
                backdrop-filter: blur(20px) saturate(1.2) !important;
                background: rgba(17, 18, 15, .88) !important;
                border-color: rgba(255, 255, 255, .1) !important;
                box-shadow: 0 18px 52px rgba(0, 0, 0, .24);
                min-height: 74px !important;
            }

            [data-web-template-family="restaurant"] [data-web-region="header"]
                .web-site-navigation > :where(.site-navigation-desktop-shell, .site-navigation-mobile-tree) {
                --site-navigation-accent: var(--site-accent);
                --site-navigation-accentText: var(--site-on-accent);
            }

            [data-web-template-family="restaurant"] .site-template-lead-form label > span {
                color: var(--site-accent) !important;
            }

            [data-web-template-family="restaurant"] [data-web-region="hero"] {
                align-items: end !important;
                min-height: 100svh !important;
                overflow: hidden;
                padding-bottom: clamp(4rem, 10vh, 7rem) !important;
                padding-top: clamp(9rem, 18vh, 13rem) !important;
            }

            [data-web-template-family="restaurant"] [data-web-region="hero"]::before {
                background:
                    linear-gradient(90deg, rgba(3, 4, 6, .86), rgba(3, 4, 6, .36) 56%, rgba(3, 4, 6, .12)),
                    linear-gradient(0deg, rgba(3, 4, 6, .86), transparent 58%);
                content: "";
                inset: 0;
                pointer-events: none;
                position: absolute;
                z-index: 0;
            }

            [data-web-template-family="restaurant"] [data-web-region="hero"] > .site-builder-grid {
                align-items: end;
                min-height: calc(100svh - clamp(13rem, 28vh, 20rem));
                position: relative;
                z-index: 1;
            }

            [data-web-template-family="restaurant"] [data-web-region="hero"] [data-document-role="heroHeading"] {
                font-size: clamp(4rem, 8vw, 8.2rem) !important;
                letter-spacing: -.045em !important;
                line-height: .84 !important;
                max-width: 940px !important;
                text-wrap: balance;
            }

            [data-web-template-family="restaurant"] [data-web-region="experience"] {
                background: var(--site-text) !important;
                color: var(--site-background);
            }

            [data-web-template-family="restaurant"] [data-web-region="experience"] :where(.site-builder-text, .site-builder-icon) {
                color: var(--site-background) !important;
            }

            [data-web-template-family="restaurant"] .site-builder-card {
                overflow: hidden;
                transition: border-color 220ms ease, box-shadow 220ms ease, transform 220ms ease;
            }

            [data-web-template-family="restaurant"] .site-builder-card:hover,
            [data-web-template-family="restaurant"] .site-builder-card:focus-within {
                border-color: color-mix(in srgb, var(--site-accent) 62%, transparent) !important;
                box-shadow: 0 24px 62px rgba(0, 0, 0, .18);
                transform: translateY(-4px);
            }

            [data-web-template-family="restaurant"] :where([data-web-region="gallery"], [data-web-region="occasions"]) .site-builder-image {
                overflow: hidden;
            }

            [data-web-template-family="restaurant"] :where([data-web-region="gallery"], [data-web-region="occasions"]) img {
                transition: filter 520ms ease, transform 760ms cubic-bezier(.2,.7,.2,1);
            }

            [data-web-template-family="restaurant"] :where([data-web-region="gallery"], [data-web-region="occasions"]) :where(.site-builder-image, .site-builder-card):hover img {
                filter: saturate(1.1) contrast(1.04);
                transform: scale(1.05);
            }

            [data-web-template-family="restaurant"] [data-restaurant-reveal="true"] {
                opacity: 0;
                transform: translateY(22px);
                transition: opacity 560ms ease, transform 560ms cubic-bezier(.2,.7,.2,1);
            }

            [data-web-template-family="restaurant"] [data-restaurant-revealed="true"],
            [data-web-template-family="restaurant"][data-web-template-editor="true"] [data-restaurant-reveal="true"] {
                opacity: 1;
                transform: none;
            }

            @container restaurant-template (max-width: 900px) {
                [data-web-template-family="restaurant"] [data-web-region="header"] {
                    min-height: 72px !important;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"] .restaurant-mobile-menu-toggle {
                    opacity: 0;
                    pointer-events: none;
                    transform: translateY(-8px);
                    transition: opacity 260ms ease, transform 260ms ease;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-header-scrolled] .restaurant-mobile-menu-toggle,
                [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-mobile-menu-open] .restaurant-mobile-menu-toggle {
                    opacity: 1;
                    pointer-events: auto;
                    transform: none;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-mobile-menu-open] {
                    background:
                        radial-gradient(circle at 88% 8%, color-mix(in srgb, var(--site-accent) 22%, transparent), transparent 32rem),
                        #08090a !important;
                }

                [data-web-template-family="restaurant"] [data-web-region="header"][data-restaurant-mobile-menu-open] [data-document-role="headerNavigation"] .site-builder-text > span {
                    color: #f8f3ea !important;
                }

                [data-web-template-family="restaurant"] [data-web-region="hero"] {
                    padding-inline: 1rem !important;
                }

                [data-web-template-family="restaurant"] [data-web-region="hero"] [data-document-role="heroHeading"] {
                    font-size: clamp(3.35rem, 16vw, 5.6rem) !important;
                }
            }

            @media (prefers-reduced-motion: reduce) {
                [data-web-template-family="restaurant"] [data-restaurant-reveal="true"] {
                    opacity: 1;
                    transform: none;
                }
            }

        `}</style>
    );
}

function withoutDuplicateFooterRoots(
    contentJson: SiteBuilderContentJson | null | undefined,
): SiteBuilderContentJson | null | undefined {
    const blocks = contentJson?.blocks;

    if (!blocks || blocks.length < 2) {
        return contentJson;
    }

    let hasFooterRoot = false;
    const uniqueBlocks = blocks.filter((block) => {
        if (block.type !== "footer") {
            return true;
        }

        if (hasFooterRoot) {
            return false;
        }

        hasFooterRoot = true;
        return true;
    });

    return uniqueBlocks.length === blocks.length
        ? contentJson
        : {
            ...contentJson,
            blocks: uniqueBlocks,
        };
}

function RestaurantFallbackPage({
    props,
    design,
    LinkComponent,
}: {
    props: TemplateRenderProps;
    design: ResolvedTemplateDesign;
    LinkComponent: TemplateLinkComponent;
}) {
    const page = props.page;

    if (!page) {
        return null;
    }

    return (
        <main
            className="px-5 py-20 sm:px-6"
            data-builder-document-region="page"
        >
            <article className="mx-auto max-w-[940px] rounded-[calc(var(--site-radius)+12px)] border border-white/10 bg-white/[0.06] p-6 leading-8 text-white/70 shadow-[0_28px_90px_rgba(0,0,0,0.24)] sm:p-10 [&_a]:text-[var(--site-accent)] [&_blockquote]:border-l-2 [&_blockquote]:border-[var(--site-accent)] [&_blockquote]:pl-5 [&_h1]:mb-5 [&_h1]:font-[var(--site-heading-font)] [&_h1]:text-5xl [&_h1]:font-semibold [&_h1]:leading-tight [&_h1]:text-white [&_h2]:mb-3 [&_h2]:mt-9 [&_h2]:font-[var(--site-heading-font)] [&_h2]:text-3xl [&_h2]:font-semibold [&_h2]:text-white [&_h3]:font-[var(--site-heading-font)] [&_h3]:text-2xl [&_h3]:text-white [&_li]:mb-2 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-5 [&_strong]:text-white [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6">
                <h1>{page.title}</h1>
                {renderTemplateContent(page, design, LinkComponent, props.leadForm, props.readOnly)}
            </article>
        </main>
    );
}

export const restaurantModernTemplate: SiteTemplateDefinition = {
    manifest: {
        code: "restaurant-modern",
        name: "Casual dining",
        type: "website",
        category: "restaurant",
        description: "Moderní šablona pro neformální restaurace a bistra s denním menu, sdílenými talíři a snadnou rezervací.",
        previewImage: "/templates/restaurant-modern/preview.webp",
        defaults: {
            colors: {
                primary: "#f7941d",
                accent: "#f7941d",
                background: "#ffffff",
                surface: "#ffffff",
                text: "#3b3b3b",
                muted: "#766f68",
                border: "rgba(59,59,59,0.22)",
            },
            typography: {
                heading: "Roboto Slab, Rockwell, Georgia, serif",
                body: "EB Garamond, Georgia, serif",
            },
            layout: {
                container: "1000px",
                sectionSpacing: "comfortable",
                radius: "0px",
            },
            components: {
                header: "builder",
                footer: "builder",
                buttons: "outline",
                forms: "line",
            },
        },
        pageTypes: {
            home: {
                label: "Restaurant homepage",
                slots: ["richContent"],
            },
            content: {
                label: "Restaurant content page",
                slots: ["richContent"],
            },
        },
        editable: {
            colors: true,
            typography: true,
            radius: true,
            header: ["builder"],
            footer: ["builder"],
            buttons: ["pill", "solid", "outline"],
        },
    },
    render: (props: TemplateRenderProps) => renderRestaurantTemplate(
        restaurantModernTemplate,
        props,
        <RestaurantSteakhouseStyles />,
    ),
};

export function renderRestaurantTemplate(
    definition: SiteTemplateDefinition,
    props: TemplateRenderProps,
    variantStyles: ReactNode = null,
) {
    const templateCode = definition.manifest.code;
    const design = mergeTemplateDesign(
        definition.manifest.defaults,
        props.designOverride ?? props.site?.templateDesign,
    );
    const LinkComponent = props.LinkComponent ?? DefaultTemplateLink;
    const globalLayout = props.page?.globalLayout ?? props.site?.globalLayout ?? null;

    return (
        <div
            data-web-template={templateCode}
            data-web-template-family="restaurant"
            data-web-template-editor={props.editorMode ? "true" : undefined}
            className="min-h-screen overflow-x-hidden bg-[var(--site-background)] font-[var(--site-body-font)] text-[var(--site-text)] antialiased"
            style={templateDesignToCssVars(design)}
        >
            <RestaurantStyles />
            {variantStyles}
            <WebGlobalBuilderRegion
                design={design}
                LinkComponent={LinkComponent}
                props={props}
                region="header"
            >
                <RestaurantMobileMenu
                    languageCode={props.page?.languageCode ?? props.site?.defaultLanguageCode}
                    templateCode={templateCode}
                />
            </WebGlobalBuilderRegion>
            <WebBuilderPageRegion
                design={design}
                fallback={(
                    <RestaurantFallbackPage
                        props={props}
                        design={design}
                        LinkComponent={LinkComponent}
                    />
                )}
                LinkComponent={LinkComponent}
                props={props}
            />
            <WebGlobalBuilderRegion
                design={design}
                document={withoutDuplicateFooterRoots(globalLayout?.footer)}
                LinkComponent={LinkComponent}
                props={props}
                region="footer"
            />
        </div>
    );
}
